import * as React from "react";
import {RefObject, useEffect, useRef, useState} from "react";
import {useLightMode} from "@/app/AppThemeContext";
import {Container, Grid2} from "@mui/material";
import {Layout, LayoutItem} from "@/lib/components/Layout";
import {Header} from "@/app/header/Header";
import Divider from "@mui/material/Divider";
import {color_blue} from "@/agentur/AgenturColors";
import {Footer} from "@/app/footer/Footer";
import {StatusFooter} from "@/app/footer/StatusFooter";
import Box from "@mui/material/Box";
import PageLoadingSpinner from "@/lib/components/PageLoadingSpinner";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {CommonI18N} from "@/translations";
import {Language, LegalDocument} from "@/api/data-contracts";
import {LANGUAGE_TO_LNG} from "@/app/locale/languageCodes";
import {fetchLegalDocumentContent, LegalDocumentLoadState} from "@/common/legal/legalDocuments";
import {LEGAL_DOCUMENT_SCOPE_CLASS, prepareLegalDocumentHtml} from "@/common/legal/legalDocumentHtml";
import {resolveApiLanguage} from "@/app/locale/anonymousLanguage";
import {compactValue, isCompactScreen, useScreenClass} from "@/lib/screen-class";

export interface LegalDocumentViewProps {
    document: LegalDocument;
}

/** Divider, link footer and status footer below the legal text. */
const LegalPageFooter = () => (
        <>
            <Divider dir="horizontal" color={color_blue[3]} sx={{borderWidth: "1.5px"}}/>
            <Footer landingPage/>
            <StatusFooter/>
        </>
);

/**
 * Writes the prepared document into the target, or empties it. The document's nodes are inserted directly as a
 * prepared fragment (meta-refresh removal, style scoping); the stored text value itself stays untouched.
 * `replaceChildren` with the fragment — instead of an `innerHTML` assignment — keeps the filtered nodes from passing
 * through the HTML parser a second time (see `prepareLegalDocumentHtml`).
 */
const writeLegalDocument = (target: HTMLDivElement | null, content?: string) => {
    if (!target) {
        return;
    }
    if (content) {
        target.replaceChildren(prepareLegalDocumentHtml(content));
    } else {
        target.replaceChildren();
    }
};

type LegalDocumentContentState = {
    contentRef: RefObject<HTMLDivElement | null>;
    effectiveLanguage: Language;
    loadState: LegalDocumentLoadState;
};

/** Loads the document in the given language and writes it imperatively into the element behind `contentRef`. */
const useLegalDocumentContent = (legalDocument: LegalDocument, language: Language): LegalDocumentContentState => {
    const contentRef = useRef<HTMLDivElement>(null);
    // Language the rendered content is actually written in — differs from `language` when the server
    // applied the German fallback for an unmaintained language. Used as the container's `lang` so
    // screen readers, browser translation and hyphenation follow the content, not the UI language.
    const [effectiveLanguage, setEffectiveLanguage] = useState<Language>(language);
    const [loadState, setLoadState] = useState<LegalDocumentLoadState>("loading");

    useEffect(() => {
        // The content is written imperatively into the DOM, so no re-render can correct a late
        // response: on a rapid language switch (de -> en -> de) the last response to arrive would win,
        // regardless of which request it belongs to. The flag discards responses of an outdated effect run.
        let isCancelled = false;

        // Drop the previous language's content right away — otherwise it stays visible (and would be
        // labelled with the new language) until the new response arrives.
        setLoadState("loading");
        contentRef.current?.replaceChildren();

        const loadHtmlContent = async () => {
            // The fetch itself is contractually throw-free, the DOM work after it is not: an unexpected
            // failure there would leave an unhandled rejection AND a spinner that never resolves, because
            // `setLoadState` would not be reached.
            try {
                const loaded = await fetchLegalDocumentContent(legalDocument, language);
                if (isCancelled) {
                    return;
                }
                setEffectiveLanguage(loaded?.effectiveLanguage ?? language);
                writeLegalDocument(contentRef.current, loaded?.content);
                setLoadState(loaded ? "loaded" : "error");
            } catch (error: unknown) {
                if (isCancelled) {
                    return;
                }
                console.error("Failed to render the legal document", error);
                contentRef.current?.replaceChildren();
                setLoadState("error");
            }
        };
        loadHtmlContent();
        return () => {
            isCancelled = true;
        };
    }, [legalDocument, language]);

    return {contentRef, effectiveLanguage, loadState};
};

/** Spinner or error text, the document itself and the link to the homepage. */
const LegalDocumentContent = ({contentRef, effectiveLanguage, loadState}: LegalDocumentContentState) => {
    const {t} = useTranslation();
    return (
            <>
                {loadState === "loading" && (
                        <Box sx={{position: "relative", minHeight: "80px"}}>
                            <PageLoadingSpinner fullScreen={false}/>
                        </Box>
                )}
                {loadState === "error" && (
                        <Box>
                            <h1>{t(CommonI18N.message.documentLoadFailed)}</h1>
                            <p>{t(CommonI18N.message.documentLoadFailedSupport)}</p>
                        </Box>
                )}
                {/* Always mounted: the content is written into this element imperatively, so the ref
                must not change between the request and the response. */}
                <Box
                        ref={contentRef}
                        className={LEGAL_DOCUMENT_SCOPE_CLASS}
                        lang={LANGUAGE_TO_LNG[effectiveLanguage]}
                        sx={{overflowWrap: compactValue("anywhere", "normal")}}
                />
                <Grid2 paddingTop={4}>
                    <Link to="/">{t(CommonI18N.action.toHomepage)}</Link>
                </Grid2>
            </>
    );
};

/**
 * Public info page rendering one legal document, loaded from the maintainable i18n text store via
 * `GET /api/legal-documents/{document}/{language}`. The content is reloaded whenever the active
 * app language changes; the German fallback for an unmaintained language happens server-side.
 */
// The prop is destructured into `legalDocument` on purpose: a local named `document` would shadow
// the global `document` object inside this component.
export const LegalDocumentView = ({document: legalDocument}: LegalDocumentViewProps) => {
    useLightMode();
    const {i18n} = useTranslation();
    const content = useLegalDocumentContent(legalDocument, resolveApiLanguage(i18n.language));
    const compact = isCompactScreen(useScreenClass());

    return (
            <Container maxWidth="xl">
                <Layout root>
                    <LayoutItem>
                        <Header/>
                    </LayoutItem>
                    <LayoutItem
                            body
                            sx={{overflowX: "hidden", maxWidth: "100%", px: {xs: 0, md: 10}, py: {xs: 0, md: 6}}}
                    >
                        <LegalDocumentContent {...content}/>
                        {compact && <LegalPageFooter/>}
                    </LayoutItem>
                    {/* Below md the footer stacks its blocks; fixed under the text at 360x800 and 800x360 it left the
                    scrolling text area 0 px high. There it scrolls with the text, from md it stays fixed as before.
                    Conditional rendering keeps every footer element once in the DOM. */}
                    {!compact && (
                            <LayoutItem>
                                <LegalPageFooter/>
                            </LayoutItem>
                    )}
                </Layout>
            </Container>
    );
};
